import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FormControl,
  IconButton,
  InputAdornment,
  OutlinedInput,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { toast } from "react-toastify";
import { logout, updateUserData } from "../../redux/authSlice";
import BlueButton from "../../components/BlueButton/BlueButton";
import GreenButton from "../../components/GreenButton/GreenButton";
import Loader from "../../components/Loader";
import api from "../../utils/api";
import "./Profile.scss";

const FormField = ({ label, ...props }) => (
  <FormControl sx={{ m: 1, width: "50%" }} className="select">
    <label className="form-label">{label}</label>
    <TextField fullWidth className="form-input" required {...props} />
  </FormControl>
);

const PasswordField = ({
  label,
  value,
  onChange,
  visibility,
  toggleVisibility,
}) => (
  <FormControl sx={{ m: 1, width: "50%" }} className="select">
    <label className="form-label">{label}</label>
    <OutlinedInput
      type={visibility ? "text" : "password"}
      value={value}
      onChange={onChange}
      inputProps={{ minLength: 8 }}
      required
      endAdornment={
        value.length > 0 && (
          <InputAdornment position="end">
            <IconButton
              onClick={toggleVisibility}
              onMouseDown={(e) => e.preventDefault()}
              edge="end"
            >
              {visibility ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        )
      }
    />
  </FormControl>
);

const MainDataView = ({ updatedUser, handleChange, handleEdit, isLoading }) => (
  <form onSubmit={handleEdit}>
    <div className="control-group">
      <FormField
        label="الاسم"
        value={updatedUser?.name}
        name="name"
        onChange={handleChange}
      />
    </div>

    <div className="control-group">
      <FormField
        label="الايميل"
        type="email"
        value={updatedUser?.email}
        name="email"
        onChange={handleChange}
      />
    </div>

    <div className="form-actions">
      <GreenButton
        text={isLoading ? "جاري التحديث..." : "حفظ"}
        size="large"
        variant="fill"
        type="submit"
        disabled={isLoading}
      />
    </div>
  </form>
);

const PasswordView = ({
  handlePassword,
  isLoading,
  passwordData,
  setPasswordData,
  passwordVisibility,
  togglePasswordVisibility,
}) => (
  <form onSubmit={handlePassword}>
    <div className="control-group">
      <PasswordField
        label="كلمة المرور الحالية"
        value={passwordData.oldPassword}
        onChange={(e) =>
          setPasswordData((prev) => ({
            ...prev,
            oldPassword: e.target.value,
          }))
        }
        visibility={passwordVisibility.old}
        toggleVisibility={() => togglePasswordVisibility("old")}
      />
    </div>

    <div className="control-group">
      <PasswordField
        label="كلمة المرور الجديدة"
        value={passwordData.password}
        onChange={(e) =>
          setPasswordData((prev) => ({ ...prev, password: e.target.value }))
        }
        visibility={passwordVisibility.new}
        toggleVisibility={() => togglePasswordVisibility("new")}
      />
      <PasswordField
        label="تاكيد كلمة المرور"
        value={passwordData.confirmPassword}
        onChange={(e) =>
          setPasswordData((prev) => ({
            ...prev,
            confirmPassword: e.target.value,
          }))
        }
        visibility={passwordVisibility.confirm}
        toggleVisibility={() => togglePasswordVisibility("confirm")}
      />
    </div>

    <div className="form-actions">
      <BlueButton
        text={isLoading ? "جاري التحديث..." : "تغير"}
        size="large"
        variant="fill"
        type="submit"
        disabled={isLoading}
      />
    </div>
  </form>
);

const Profile = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth);
  console.log(user);

  const [currentView, setCurrentView] = useState("main");
  const [updatedUser, setUpdatedUser] = useState(user);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    password: "",
    confirmPassword: "",
  });
  const [passwordVisibility, setPasswordVisibility] = useState({
    old: false,
    new: false,
    confirm: false,
  });

  const isMobile = useMediaQuery("(max-width:767px)");

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);

      const updatedData = {
        name: updatedUser?.name,
        email: updatedUser?.email,
        phoneNumber: updatedUser?.phoneNumber,
        companyName: updatedUser?.companyName,
      };

      const response = await api.patch(`/users/updateMe`, updatedData);
      dispatch(updateUserData(response.data));
      toast.success("تم تحديث البيانات بنجاح");
    } catch (error) {
      console.error("Update error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePassword = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      await api.patch(`/auth/updatePassword`, passwordData);
      toast.success("تم تحديث كلمة المرور بنجاح برجاء تسجيل الدخول مرة اخري", {
        autoClose: 8000, // 8 seconds
      });
      dispatch(logout());
    } catch (error) {
      console.error("Password update error:", error);
    } finally {
      setIsLoading(false);
      setPasswordData({
        oldPassword: "",
        password: "",
        confirmPassword: "",
      });
    }
  };

  const handleChange = (e) => {
    setUpdatedUser((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  if (isLoading) return <Loader />;

  return (
    <div className="profile">
      <div className="container">
        <div className="tabs">
          <BlueButton
            onClick={() => setCurrentView("main")}
            text="البيانات الاساسية"
            size={isMobile ? "full" : "large"}
            variant={currentView === "main" ? "fill" : "outlined"}
          />
          <BlueButton
            onClick={() => setCurrentView("password")}
            text="تغيير كلمة المرور"
            size={isMobile ? "full" : "large"}
            variant={currentView === "password" ? "fill" : "outlined"}
          />
        </div>

        {currentView === "main" && (
          <MainDataView
            updatedUser={updatedUser}
            handleChange={handleChange}
            handleEdit={handleEdit}
            isLoading={isLoading}
          />
        )}
        {currentView === "password" && (
          <PasswordView
            handlePassword={handlePassword}
            isLoading={isLoading}
            passwordData={passwordData}
            setPasswordData={setPasswordData}
            passwordVisibility={passwordVisibility}
            togglePasswordVisibility={togglePasswordVisibility}
          />
        )}
      </div>
    </div>
  );
};

export default Profile;
